import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PayrollRecords.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { calculatePeriodsForYear } from '../utils/payroll-calculator';
import { apiFetch } from '../utils/api';
import { FaInfoCircle, FaHeadset, FaExclamationTriangle } from 'react-icons/fa';
import { Icons } from '../components/Icons';

// --- CUSTOM CALENDAR-STYLE PERIOD SELECTOR WIDGET ---
const PeriodWidget = ({ periods, selectedId, onSelect, placeholder, disabled, viewYear }) => {
  const [isOpen, setIsOpen] = useState(false);
  // NEW: Tracks whether we are viewing the list of periods or the month selection grid
  const [pickerMode, setPickerMode] = useState('period'); // 'period' | 'month'
  
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setPickerMode('period'); // Reset view state when closed
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [viewMonth, setViewMonth] = useState(() => {
    const selected = periods.find(p => p.id === selectedId);
    return selected ? new Date(selected.end_date + 'T00:00:00').getUTCMonth() : new Date().getMonth();
  });

  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  const currentMonthPeriods = periods.filter(p => {
    if (!p.end_date) return false;
    return new Date(p.end_date + 'T00:00:00').getUTCMonth() === viewMonth;
  });

  const selectedPeriod = periods.find(p => p.id === selectedId);

  return (
    <div className="position-relative w-100" ref={dropdownRef}>
      
      {/* Trigger Button */}
      <button 
        type="button"
        className={`form-select text-start shadow-sm d-flex justify-content-between align-items-center ${disabled ? 'bg-light text-muted' : 'bg-white'} ${!selectedPeriod && !disabled ? 'text-muted fw-normal' : 'fw-semibold'}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '38px' }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-truncate pe-2 pr-mono">
          {selectedPeriod ? `${selectedPeriod.period_name} (${selectedPeriod.start_date} to ${selectedPeriod.end_date})` : placeholder}
        </span>
        <Icons.ChevronDown />
      </button>

      {/* Dropdown Container */}
      {isOpen && (
        <div className="position-absolute top-100 start-0 mt-1 bg-white border border-light rounded-3 shadow-lg w-100" style={{ minWidth: '320px', zIndex: 1050, overflow: 'hidden' }}>
           
           {/* Header / Navigation */}
           <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
              <button 
                type="button" 
                className="btn btn-sm btn-white border shadow-sm text-primary fw-bold d-flex align-items-center justify-content-center" 
                onClick={() => setViewMonth(m => Math.max(0, m - 1))} 
                disabled={viewMonth === 0 || pickerMode === 'month'}
                style={{ width: '32px', height: '32px', transition: 'all 0.2s' }}
                aria-label="Previous Month"
              >
                &lt;
              </button>
              
              {/* Month Toggle Button */}
              <button 
                type="button"
                className="btn btn-sm btn-light fw-bold text-dark text-uppercase border-0 shadow-none px-3"
                onClick={() => setPickerMode(mode => mode === 'period' ? 'month' : 'period')}
                style={{ letterSpacing: '0.5px' }}
                title="Click to jump to a specific month"
              >
                {MONTHS[viewMonth]} {viewYear} <span className="ms-1 fs-6">{pickerMode === 'month' ? '▴' : '▾'}</span>
              </button>

              <button 
                type="button" 
                className="btn btn-sm btn-white border shadow-sm text-primary fw-bold d-flex align-items-center justify-content-center" 
                onClick={() => setViewMonth(m => Math.min(11, m + 1))} 
                disabled={viewMonth === 11 || pickerMode === 'month'}
                style={{ width: '32px', height: '32px', transition: 'all 0.2s' }}
                aria-label="Next Month"
              >
                &gt;
              </button>
           </div>
           
           {/* Body */}
           <div className="p-2" style={{ maxHeight: '310px', overflowY: 'auto' }}>
              
              {pickerMode === 'month' ? (
                /* Month Selector Grid (3x4) */
                <div className="row g-2 p-2">
                  {SHORT_MONTHS.map((month, index) => (
                    <div className="col-4" key={month}>
                      <button
                        type="button"
                        className={`btn w-100 fw-semibold rounded-3 py-3 transition-all ${index === viewMonth ? 'btn-primary shadow-sm' : 'btn-light text-dark'}`}
                        onClick={() => {
                          setViewMonth(index);
                          setPickerMode('period');
                        }}
                      >
                        {month}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                /* Period List */
                <div className="d-flex flex-column gap-2 p-1">
                  {currentMonthPeriods.length === 0 ? (
                    <div className="text-center text-muted small py-5 d-flex flex-column align-items-center">
                      <Icons.CalendarEmpty />
                      <span>No periods end in {MONTHS[viewMonth]}.</span>
                    </div>
                  ) : (
                    currentMonthPeriods.map(p => {
                      const isSelected = selectedId === p.id;
                      
                      return (
                        <div 
                          key={p.id}
                          onClick={() => { onSelect(p.id); setIsOpen(false); }}
                          className={`p-3 rounded-3 border transition-all position-relative ${isSelected ? 'bg-primary bg-opacity-10 border-primary shadow-sm' : 'bg-white border-light'}`}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => { if(!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                          onMouseLeave={(e) => { if(!isSelected) e.currentTarget.style.backgroundColor = '#ffffff'; }}
                        >
                          {/* Accent bar for active state */}
                          {isSelected && <div className="position-absolute top-0 start-0 h-100 bg-primary rounded-start" style={{ width: '4px' }}></div>}
                          
                          <div className="d-flex justify-content-between align-items-center mb-2">
                             <span className={`fw-bold ${isSelected ? 'text-primary' : 'text-dark'}`}>{p.period_name}</span>
                             <span className={`badge ${isSelected ? 'bg-primary text-white shadow-sm' : 'bg-light text-dark border'} small pr-mono`}>Pay Date: {p.pay_date}</span>
                          </div>
                          
                          <div className={`small ${isSelected ? 'text-primary text-opacity-75' : 'text-muted'}`}>
                            <span className="d-inline-block me-1">Cycle:</span> 
                            <span className={`fw-medium pr-mono ${isSelected ? 'text-primary' : 'text-dark'}`}>{p.start_date}</span> 
                            <span className="mx-1">to</span> 
                            <span className={`fw-medium pr-mono ${isSelected ? 'text-primary' : 'text-dark'}`}>{p.end_date}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
           </div>
        </div>
      )}
    </div>
  );
};
// --- END CUSTOM WIDGET ---

function PayrollRecords({ org, user }) {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [anchorDates, setAnchorDates] = useState({});
  
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [viewFormData, setViewFormData] = useState(null);
  const [isSalariedEmployee, setIsSalariedEmployee] = useState(false);
  
  const [subscriptionError, setSubscriptionError] = useState(false);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  
  const [filters, setFilters] = useState({
    frequency: '', employee_id: '', period_id: '',
  });
  
  const [formData, setFormData] = useState({
    pay_frequency: '', employee_id: '', payroll_period_id: '', gross_pay: '',
    hours_worked: '', hourly_rate: '', pay_date: '', period_start_date: '', bonus: '',
    period_end_date: '', additional_allowance: '', ytd_gross: '', ytd_cpp: '', ytd_cpp_additional: '',
    ytd_ei: '', union_dues: '', rrsp_contribution: '', health_insurance: '',
    other_deductions: '', status: 'Draft', notes: '',
  });

  // --- Table scroll-fade indicators (mobile horizontal scroll hint) ---
  const tableScrollRef = useRef(null);
  const [scrollShadow, setScrollShadow] = useState({ left: false, right: false });

  const updateScrollShadow = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setScrollShadow({
      left: scrollLeft > 4,
      right: scrollLeft < scrollWidth - clientWidth - 4,
    });
  }, []);

  const normalizeFreq = (freq) => (freq || '').replace(/-/g, '').toLowerCase();

  const triggerNotification = (message, type = 'success') => {
    const options = { position: "top-right", autoClose: 5000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true };
    switch (type) {
      case 'success': toast.success(message, options); break;
      case 'danger': toast.error(message, options); break;
      case 'warning': toast.warn(message, options); break;
      case 'info': toast.info(message, options); break;
      default: toast(message, options); break;
    }
  };

  // Load display + data typefaces once (shared with Employee Directory's "Ledger" system)
  useEffect(() => {
    if (document.getElementById('ledger-font-link')) return;
    const link = document.createElement('link');
    link.id = 'ledger-font-link';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  // Lock body scroll if Drawer or Modal is open
  useEffect(() => {
    if (showForm || viewRecord || subscriptionError) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showForm, viewRecord, subscriptionError]);

  useEffect(() => {
    if (org) {
      fetchEmployees();
      fetchAnchorDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  useEffect(() => {
    if (org?.id) fetchPayrollRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org, filters, viewYear]);

  // Re-check scroll shadows whenever the underlying data or viewport changes size,
  // since the table's scrollWidth/clientWidth only settle after this render commits.
  useEffect(() => {
    const raf = requestAnimationFrame(updateScrollShadow);
    window.addEventListener('resize', updateScrollShadow);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateScrollShadow);
    };
  }, [payrollRecords, filters, viewYear, updateScrollShadow]);

  const fetchAnchorDates = async () => {
    if (!org?.id) return;
    try {
      const data = await apiFetch(`/organizations/${org.id}/payroll-anchors`, org.tenantId);
      setAnchorDates(data);
    } catch (error) {
      triggerNotification('Could not load schedule configurations.', 'danger');
    }
  };

  const fetchPayrollRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.period_id) params.append('period_id', filters.period_id);
      params.append('year', viewYear); 

      const data = await apiFetch(`/organizations/${org.id}/payroll-records?${params.toString()}`, org.tenantId);
      setPayrollRecords(data || []);
    } catch (error) {
      triggerNotification('Failed to fetch records.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await apiFetch(`/organizations/${org.id}/employees`, org.tenantId);
      setEmployees(data || []);
    } catch (error) {
      triggerNotification('Failed to fetch employees.', 'danger');
    }
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await apiFetch(`/payroll-records/${recordId}`, org.tenantId, {
        method: 'PUT', body: { status: newStatus },
      });
      fetchPayrollRecords();
      triggerNotification('Status updated successfully.');
    } catch (error) {
      triggerNotification('Network error updating status.', 'danger');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      if (name === 'pay_frequency') {
        newData.employee_id = '';
        newData.payroll_period_id = '';
        newData.period_start_date = '';
        newData.period_end_date = '';
        newData.pay_date = '';
        newData.gross_pay = '';
      }

      if (name === 'employee_id') {
        const emp = employees.find(empl => empl.id === parseInt(value));
        if (emp) {
          newData.hourly_rate = emp.hourly_rate || '';
          setIsSalariedEmployee(emp.pay_group === 'Salary');
          newData.pay_frequency = emp.pay_frequency || newData.pay_frequency;
          const pay_group = emp.pay_group || 'Hourly';

          if (pay_group === 'Salary' && emp.salary) {
            const periods = { weekly: 52, biweekly: 26, 'semi-monthly': 24, monthly: 12 }[emp.pay_frequency] || 26;
            const baseSalary = emp.salary / periods; // eslint-disable-line
            const allowance = parseFloat(newData.additional_allowance) || 0;
            const bonus = parseFloat(newData.bonus) || 0;
            newData.gross_pay = (baseSalary + allowance + bonus).toFixed(2);
          } else if (pay_group === 'Hourly' && emp.hourly_rate && newData.hours_worked) {
            const baseHourly = parseFloat(newData.hours_worked) * parseFloat(emp.hourly_rate);
            const allowance = parseFloat(newData.additional_allowance) || 0;
            const bonus = parseFloat(newData.bonus) || 0;
            newData.gross_pay = (baseHourly + allowance + bonus).toFixed(2);
          } else {
            newData.hours_worked = ''; // Clear hours for salaried employees
            newData.gross_pay = (parseFloat(newData.additional_allowance) || 0) + (parseFloat(newData.bonus) || 0) || '';
          }
        }
      }

      if (name === 'hours_worked' || name === 'additional_allowance' || name === 'bonus') {
        if (newData.hourly_rate && newData.hours_worked) {
          const basePay = parseFloat(newData.hours_worked) * parseFloat(newData.hourly_rate);
          const allowance = parseFloat(newData.additional_allowance) || 0; const bonus = parseFloat(newData.bonus) || 0;
          newData.gross_pay = (basePay + allowance + bonus).toFixed(2);
        }
      }
      return newData;
    });
  };

  const formPeriods = (formData.pay_frequency && anchorDates[formData.pay_frequency])
    ? calculatePeriodsForYear(anchorDates[formData.pay_frequency], viewYear, formData.pay_frequency)
    : [];

  const handlePeriodChange = (selectedId) => {
    const period = formPeriods.find(p => p.id === selectedId);

    if (period && user && user.enabled_date && user.expiry_date) {
      const payDate = new Date(period.pay_date + 'T00:00:00');
      const enabledDate = new Date(user.enabled_date + 'T00:00:00');
      const expiryDate = new Date(user.expiry_date + 'T00:00:00');

      if (payDate < enabledDate || payDate > expiryDate) {
        setSubscriptionError(true);
        // Do not update the form with the invalid period
        setFormData(prev => ({ ...prev, payroll_period_id: '' }));
        return;
      }
    }

    // If validation passes or is not applicable
    setSubscriptionError(false);

    setFormData(prev => ({
      ...prev,
      payroll_period_id: period ? period.id : '',
      pay_frequency: period ? period.pay_frequency || prev.pay_frequency : prev.pay_frequency,
      pay_date: period ? period.pay_date : prev.pay_date,
      period_start_date: period ? period.start_date : prev.period_start_date,
      period_end_date: period ? period.end_date : prev.period_end_date,
    }));
  };
  
  const filterPeriods = (filters.frequency && anchorDates[filters.frequency])
    ? calculatePeriodsForYear(anchorDates[filters.frequency], viewYear, filters.frequency)
    : [];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => { // eslint-disable-line no-unused-vars
      const newFilters = { ...prev, [name]: value };
      
      if (name === 'employee_id' && value) {
        const emp = employees.find(e => e.id === parseInt(value));
        if (emp && emp.pay_frequency) newFilters.frequency = emp.pay_frequency;
      } else if (name === 'period_id' && value) {
        const period = filterPeriods.find(p => p.id === value);
        if (period && period.pay_frequency) newFilters.frequency = period.pay_frequency;
      } 
      else if (name === 'frequency' && value) {
        const emp = employees.find(e => e.id == parseInt(newFilters.employee_id)); // eslint-disable-line eqeqeq
        if (emp && normalizeFreq(emp.pay_frequency) !== normalizeFreq(value)) {
          newFilters.employee_id = '';
        }
        newFilters.period_id = ''; 
      }
      return newFilters;
    });

    const normalizeFreq = (freq) => (freq || '').replace(/-/g, '').toLowerCase();
  };

  const resetForm = () => {
    setFormData({
      pay_frequency: '', employee_id: '', payroll_period_id: '', gross_pay: '',
      hours_worked: '', hourly_rate: '', pay_date: '', period_start_date: '', additional_allowance: '', bonus: '',
      period_end_date: '', ytd_gross: '', ytd_cpp: '', ytd_cpp_additional: '',
      ytd_ei: '', union_dues: '', rrsp_contribution: '', health_insurance: '',
      other_deductions: '', status: 'Draft', notes: '',
    });
    setEditRecord(null);
    setIsSalariedEmployee(false);
    setShowForm(false);
  };

  const handleEditClick = (record) => {
    setEditRecord(record);
    // When editing, ensure the viewYear is set to the year of the record being edited.
    if (record.pay_date) {
      setViewYear(new Date(record.pay_date).getFullYear());
    }

    const employee = employees.find(e => e.id === parseInt(record.employee_id));
    setIsSalariedEmployee(employee?.pay_group === 'Salary');
    const pay_freq = employee?.pay_frequency || record.pay_frequency || '';


    setFormData({
      pay_frequency: (pay_freq || ''),
      employee_id: record.employee_id || '',
      payroll_period_id: record.payroll_period_id || '',
      gross_pay: record.gross_pay || '',
      hours_worked: record.hours_worked || '',
      hourly_rate: record.hourly_rate || '',
      bonus: record.bonus || '',
      additional_allowance: record.additional_allowance || '',
      pay_date: record.pay_date || '',
      period_start_date: record.period_start_date || '',
      period_end_date: record.period_end_date || '',
      ytd_gross: '', ytd_cpp: '', ytd_cpp_additional: '', ytd_ei: '',
      union_dues: record.union_dues || '',
      rrsp_contribution: record.rrsp_contribution || '',
      health_insurance: record.health_insurance || '',
      other_deductions: record.other_deductions || '',
      status: record.status || 'Draft',
      notes: record.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.gross_pay) {
      triggerNotification('Employee and Gross Pay fields are required.', 'warning');
      return;
    }

    // --- UI-side Duplication Check ---
    if (!editRecord && formData.employee_id && formData.payroll_period_id) {
      const isDuplicate = payrollRecords.some(record => 
        record.employee_id === formData.employee_id && 
        record.payroll_period_id === formData.payroll_period_id &&
        (!editRecord || record.id !== editRecord.id) // If editing, ensure it's not a check against itself
      );
      if (isDuplicate) {
        triggerNotification('A record for this employee and period already exists.', 'warning');
        return;
      }
    }

    const endpoint = editRecord ? `/payroll-records/${editRecord.id}` : '/payroll-records';
    const method = editRecord ? 'PUT' : 'POST';
    const body = {
      organization_id: org.id,
      ...formData,
      payroll_period_id: formData.payroll_period_id || null,
      gross_pay: parseFloat(formData.gross_pay),
      hours_worked: formData.hours_worked ? parseFloat(formData.hours_worked) : null,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      bonus: formData.bonus ? parseFloat(formData.bonus) : 0,
      additional_allowance: formData.additional_allowance ? parseFloat(formData.additional_allowance) : 0,
      ytd_gross: parseFloat(formData.ytd_gross || 0),
      ytd_cpp: parseFloat(formData.ytd_cpp || 0),
      ytd_cpp_additional: parseFloat(formData.ytd_cpp_additional || 0),
      ytd_ei: parseFloat(formData.ytd_ei || 0),
      union_dues: parseFloat(formData.union_dues || 0),
      rrsp_contribution: parseFloat(formData.rrsp_contribution || 0),
      health_insurance: parseFloat(formData.health_insurance || 0),
      other_deductions: parseFloat(formData.other_deductions || 0),
    };

    try {
      await apiFetch(endpoint, org.tenantId, { method, body });
      triggerNotification(`Record successfully ${editRecord ? 'updated' : 'processed'}.`);
      resetForm();
      fetchPayrollRecords();
    } catch (error) {
      triggerNotification(`Network error while saving record.`, 'danger');
    }
  };

  const handleDownloadPaystub = async (record) => {
    const recordId = record.id;
    let empName = getEmployeeName(record.employee_id);
    empName = empName.replace(/\s+/g, '_'); // Replace spaces with underscores for filename
    const payDate = record.pay_date || record.period_end_date || '';
    try {
      const response = await apiFetch(`/payroll-records/${recordId}/paystub`, org.tenantId, { parseResponse: false });
      if (response) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PayStub_${empName}_${payDate}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        triggerNotification('Paystub downloaded successfully.');
      } else {
        triggerNotification('Failed to generate paystub.', 'danger');
      }
    } catch (error) {
      triggerNotification('Network error downloading paystub.', 'danger');
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to permanently delete this payroll record?')) return;
    try {
      await apiFetch(`/payroll-records/${recordId}`, org.tenantId, { method: 'DELETE' });
      triggerNotification('Record deleted successfully.');
      fetchPayrollRecords();
    } catch (error) {
      triggerNotification('Error deleting record.', 'danger');
    }
  };

  const handleViewRecordEdit = (e) => {
    const { name, value } = e.target;
    setViewFormData(prev => {
      const newFormData = { ...prev, [name]: parseFloat(value) || 0 };
      const total_deductions = 
        newFormData.federal_tax_deduction +
        newFormData.provincial_tax_deduction +
        newFormData.cpp_contribution +
        newFormData.ei_premium;
      const net_pay = newFormData.gross_pay - total_deductions;
      return { ...newFormData, total_deductions, net_pay };
    });
  };

  const handleViewRecordSave = async () => {
    if (!viewFormData || !viewRecord) return;
    try {
      await apiFetch(`/payroll-records/${viewRecord.id}`, org.tenantId, {
        method: 'PUT',
        body: { ...viewFormData, status: viewRecord.status, notes: viewRecord.notes },
      });

      setViewRecord(null); setViewFormData(null);
      fetchPayrollRecords();
      triggerNotification('Tax breakdown updated successfully.');
    } catch (error) {
      triggerNotification('Error updating tax record.', 'danger');
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  const filteredRecords = payrollRecords.filter(record => {
    if (filters.frequency && normalizeFreq(record.pay_frequency) !== normalizeFreq(filters.frequency)) return false;
    const recordDate = record.period_end_date || record.pay_date;
    return recordDate && recordDate.startsWith(viewYear.toString());
  });
  
  const totalGross = filteredRecords.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
  const totalNet = filteredRecords.reduce((sum, r) => sum + (r.net_pay || 0), 0);

  if (!org) return null;

  return (
    <div className="container-fluid py-4 px-md-4" style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <ToastContainer />
      {/* Subscription Error Overlay */}
      {subscriptionError && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 1060, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
        >
          <div className="card shadow-lg border-0 rounded-4 text-center" style={{ maxWidth: '520px', animation: 'fadeInUp 0.3s ease-out' }}>
            <style>{`
              @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
            <div className="card-body p-4 p-md-5">
              <div className="text-danger mb-4" style={{ fontSize: '4rem' }}>
                <FaExclamationTriangle />
              </div>
              <h2 className="h4 fw-bold text-dark mb-3">Subscription Period Mismatch</h2>
              <p className="text-muted mb-4 px-3">
                The selected time period is not enrolled in your subscription. Please reach out to the OrientalPay admin for any assistance needed on the subscription.
              </p>
              <div className="d-flex gap-2">
                <a 
                  href="https://orientalpay.ca/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-light border fw-semibold flex-fill d-flex align-items-center justify-content-center gap-2 text-dark text-decoration-none"
                >
                  <FaInfoCircle className="text-muted" /> View Pricing
                </a>
                <a 
                  href="https://portal.orientalpay.ca/support"
                  target="_self"
                  rel="noopener noreferrer"
                  className="btn btn-light border fw-semibold flex-fill d-flex align-items-center justify-content-center gap-2 text-dark text-decoration-none"
                >
                  <FaHeadset className="text-muted" /> Contact Support
                </a>
              </div>
              <button className="btn btn-outline-secondary w-100 mt-3" onClick={() => setSubscriptionError(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center mb-1">
            <Icons.Document />
            <h1 className="h4 fw-bold text-dark mb-0">Payroll Records</h1>
          </div>
          <p className="text-muted small mb-0 mt-1">Manage, track, and process employee payroll disbursements.</p>
        </div>
        <button 
          className="btn btn-primary d-flex align-items-center justify-content-center fw-semibold px-4 shadow-sm"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          <Icons.Plus /> Process New Record
        </button>
      </div>

      {/* Metrics Dashboard */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-body p-4">
              <h6 className="text-muted fw-semibold text-uppercase mb-2 tracking-wide" style={{ fontSize: '0.75rem' }}>Total Gross Payroll</h6>
              <h3 className="fw-bold mb-0 text-dark pr-mono">${totalGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-body p-4">
              <h6 className="text-muted fw-semibold text-uppercase mb-2 tracking-wide" style={{ fontSize: '0.75rem' }}>Total Net Paid</h6>
              <h3 className="fw-bold mb-0 text-success pr-mono">${totalNet.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-body p-4">
              <h6 className="text-muted fw-semibold text-uppercase mb-2 tracking-wide" style={{ fontSize: '0.75rem' }}>Processed Records</h6>
              <h3 className="fw-bold mb-0 text-dark">{filteredRecords.length} <span className="fs-6 text-muted fw-normal">this year</span></h3>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
        <div className="card-body p-3 d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center border rounded-3 px-3 py-1 bg-light flex-shrink-0">
            <label className="fw-semibold me-2 text-muted small text-uppercase mb-0">Year:</label>
            <select
              className="form-select form-select-sm border-0 fw-bold bg-transparent shadow-none"
              value={viewYear}
              onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
              style={{ minWidth: '80px', cursor: 'pointer' }}
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="flex-grow-1" style={{ maxWidth: '200px' }}>
            <select className="form-select shadow-sm" name="frequency" value={filters.frequency} onChange={handleFilterChange}>
              <option value="">All Frequencies</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="semi-monthly">Semi-monthly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          
          <div className="flex-grow-1" style={{ maxWidth: '220px' }}>
            <select className="form-select shadow-sm" name="employee_id" value={filters.employee_id} onChange={handleFilterChange}>
              <option value="">All Employees</option>
              {employees
                .filter(emp => !filters.frequency || normalizeFreq(emp.pay_frequency) === normalizeFreq(filters.frequency))
                .map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
            </select>
          </div>

          <div className="flex-grow-1" style={{ minWidth: '250px' }}>
            <PeriodWidget 
              periods={filterPeriods} selectedId={filters.period_id} 
              onSelect={(id) => handleFilterChange({ target: { name: 'period_id', value: id }})} 
              placeholder={filters.frequency ? 'Filter by Payroll Period' : 'Select Frequency First'}
              disabled={!filters.frequency} viewYear={viewYear}
            />
          </div>
        </div>
      </div>

      {/* Main Data Table */}
      <div className="card border-0 shadow-sm rounded-3 bg-white">
        <div className="card-body p-0">
          {loading ? (
            <div className="p-5 text-center text-muted d-flex flex-column align-items-center">
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <span>Loading payroll records...</span>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-5 text-center text-muted">
              <Icons.EmptyBox />
              <h5 className="fw-bold text-dark mt-2">No records found</h5>
              <p className="mb-0">Try adjusting your filters or create a new record for {viewYear}.</p>
            </div>
          ) : (
            <>
              {/* Desktop / tablet: full table, shown at >= 768px */}
              <div className="pr-table-wrap">
                <div className="table-scroll-wrap position-relative">
                  <div
                    className={`scroll-fade scroll-fade-left ${scrollShadow.left ? 'is-visible' : ''}`}
                    aria-hidden="true"
                  ></div>

                  <div
                    className="table-responsive"
                    ref={tableScrollRef}
                    onScroll={updateScrollShadow}
                  >
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light text-secondary border-bottom text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                        <tr>
                          <th className="px-4 py-3 border-0">Employee</th>
                          <th className="py-3 border-0">Period</th>
                          <th className="py-3 border-0 text-end">Gross Pay</th>
                          <th className="py-3 border-0 text-end">Deductions</th>
                          <th className="py-3 border-0 text-end">Net Pay</th>
                          <th className="py-3 border-0 text-center">Status</th>
                          <th className="px-4 py-3 border-0 text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRecords.map(record => (
                          <tr key={record.id} className="border-bottom">
                            <td className="px-4 py-3">
                              <div className="fw-bold text-primary">{getEmployeeName(record.employee_id)}</div>
                              <div className="small text-muted pr-mono">Paid: {record.pay_date}</div>
                            </td>
                            <td className="py-3">
                              <div className="small text-dark fw-medium pr-mono">{record.period_start_date} <span className="text-muted mx-1">to</span> {record.period_end_date}</div>
                              <div className="small text-muted text-capitalize">{record.pay_frequency}</div>
                            </td>
                            <td className="py-3 text-end fw-medium text-dark pr-mono">
                              ${record.gross_pay?.toFixed(2)}
                              {record.vacation_pay > 0 && (
                                <div className="small text-muted" style={{ fontSize: '0.7rem' }}>+Vac (${record.vacation_pay?.toFixed(2)})</div>
                              )}
                            </td>
                            <td className="py-3 text-end text-danger fw-medium pr-mono">
                              -${record.total_deductions?.toFixed(2)}
                            </td>
                            <td className="py-3 text-end text-success fw-bold bg-success bg-opacity-10 border-start border-success border-opacity-10 pr-mono">
                              ${record.net_pay?.toFixed(2)}
                            </td>
                            <td className="py-3 text-center">
                               <select
                                  className={`form-select form-select-sm fw-bold border-0 mx-auto rounded-pill text-center shadow-sm ${
                                    record.status === 'Final' ? 'bg-success text-white' : 'bg-warning text-dark'
                                  }`}
                                  value={record.status}
                                  onChange={(e) => handleStatusChange(record.id, e.target.value)}
                                  style={{ width: '100px', cursor: 'pointer', appearance: 'none' }}
                                >
                                  <option value="Draft">DRAFT</option>
                                  <option value="Final">FINAL</option>
                                </select>
                            </td>
                            <td className="px-4 py-3 text-end">
                              <div className="dropdown">
                                <button className="btn btn-light btn-sm px-3 fw-medium border shadow-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                                  Manage
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end shadow border-0 mt-1">
                                  <li>
                                    <button className="dropdown-item py-2 d-flex align-items-center" onClick={() => handleEditClick(record)} disabled={record.status === 'Final'}>
                                      <Icons.Edit /> Edit Details
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item py-2 d-flex align-items-center" onClick={() => {
                                      setViewRecord(record);
                                      setViewFormData({
                                        gross_pay: record.gross_pay || 0, federal_tax_deduction: record.federal_tax_deduction || 0,
                                        provincial_tax_deduction: record.provincial_tax_deduction || 0, cpp_contribution: record.cpp_contribution || 0,
                                        ei_premium: record.ei_premium || 0, total_deductions: record.total_deductions || 0, net_pay: record.net_pay || 0,
                                      });
                                    }}>
                                      <Icons.Calculator /> Tax Breakdown
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item py-2 d-flex align-items-center" onClick={() => handleDownloadPaystub(record)}>
                                      <Icons.Download /> Download Paystub
                                    </button>
                                  </li>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li>
                                    <button className="dropdown-item py-2 text-danger fw-medium d-flex align-items-center" onClick={() => handleDelete(record.id)} disabled={record.status === 'Final'}>
                                      <Icons.Trash /> Delete Record
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className={`scroll-fade scroll-fade-right ${scrollShadow.right ? 'is-visible' : ''}`}
                    aria-hidden="true"
                  ></div>
                  <div className={`scroll-hint ${scrollShadow.right && !scrollShadow.left ? 'is-visible' : ''}`}>
                    Swipe for more <span aria-hidden="true">&rarr;</span>
                  </div>
                </div>
              </div>

              {/* Mobile: card list, shown below 768px */}
              <div className="pr-cards">
                {filteredRecords.map(record => (
                  <div className="pr-record-card" key={record.id}>
                    <div className="pr-record-card-top">
                      <div>
                        <div className="fw-bold text-primary">{getEmployeeName(record.employee_id)}</div>
                        <div className="small text-muted pr-mono">Paid: {record.pay_date}</div>
                      </div>
                      <select
                        className={`form-select form-select-sm fw-bold border-0 rounded-pill text-center shadow-sm pr-status-select ${
                          record.status === 'Final' ? 'bg-success text-white' : 'bg-warning text-dark'
                        }`}
                        value={record.status}
                        onChange={(e) => handleStatusChange(record.id, e.target.value)}
                        style={{ cursor: 'pointer', appearance: 'none' }}
                      >
                        <option value="Draft">DRAFT</option>
                        <option value="Final">FINAL</option>
                      </select>
                    </div>

                    <div className="small text-dark fw-medium mt-3 pr-mono">
                      {record.period_start_date} <span className="text-muted mx-1">to</span> {record.period_end_date}
                    </div>
                    <div className="small text-muted text-capitalize">{record.pay_frequency}</div>

                    <div className="pr-record-card-figures">
                      <div>
                        <span className="pr-figure-label">Gross</span>
                        <div className="fw-medium text-dark pr-mono">
                          ${record.gross_pay?.toFixed(2)}
                          {record.vacation_pay > 0 && (
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>+Vac (${record.vacation_pay?.toFixed(2)})</div>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="pr-figure-label">Deductions</span>
                        <div className="fw-medium text-danger pr-mono">-${record.total_deductions?.toFixed(2)}</div>
                      </div>
                      <div>
                        <span className="pr-figure-label">Net Pay</span>
                        <div className="fw-bold text-success pr-mono">${record.net_pay?.toFixed(2)}</div>
                      </div>
                    </div>

                    <div className="pr-record-card-footer">
                      <div className="dropdown">
                        <button className="btn btn-light btn-sm px-3 fw-medium border shadow-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
                          Manage
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow border-0 mt-1">
                          <li>
                            <button className="dropdown-item py-2 d-flex align-items-center" onClick={() => handleEditClick(record)} disabled={record.status === 'Final'}>
                              <Icons.Edit /> Edit Details
                            </button>
                          </li>
                          <li>
                            <button className="dropdown-item py-2 d-flex align-items-center" onClick={() => {
                              setViewRecord(record);
                              setViewFormData({
                                gross_pay: record.gross_pay || 0, federal_tax_deduction: record.federal_tax_deduction || 0,
                                provincial_tax_deduction: record.provincial_tax_deduction || 0, cpp_contribution: record.cpp_contribution || 0,
                                ei_premium: record.ei_premium || 0, total_deductions: record.total_deductions || 0, net_pay: record.net_pay || 0,
                              });
                            }}>
                              <Icons.Calculator /> Tax Breakdown
                            </button>
                          </li>
                          <li>
                            <button className="dropdown-item py-2 d-flex align-items-center" onClick={() => handleDownloadPaystub(record.id)}>
                              <Icons.Download /> Download Paystub
                            </button>
                          </li>
                          <li><hr className="dropdown-divider" /></li>
                          <li>
                            <button className="dropdown-item py-2 text-danger fw-medium d-flex align-items-center" onClick={() => handleDelete(record.id)} disabled={record.status === 'Final'}>
                              <Icons.Trash /> Delete Record
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

{/* Side Drawer Overlay for Create/Edit */}
      {showForm && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-end" 
          style={{ zIndex: 1040 }}
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" 
            style={{ transition: 'opacity 0.3s' }} 
            onClick={resetForm}
            aria-hidden="true"
          ></div>
          
          {/* Sliding Panel */}
          <div 
            className="position-relative h-100 bg-white shadow-lg d-flex flex-column" 
            style={{ width: '100%', maxWidth: '600px', zIndex: 1050, animation: 'slideInRight 0.3s ease-out forwards' }}
          >
            <style>{`
              @keyframes slideInRight { 
                from { transform: translateX(100%); } 
                to { transform: translateX(0); } 
              }
              /* Subtle pulse for calculated fields */
              .calc-pulse { animation: pulse 2s infinite; }
              @keyframes pulse {
                0% { opacity: 0.8; }
                50% { opacity: 1; }
                100% { opacity: 0.8; }
              }
            `}</style>

            {/* Drawer Header */}
            <div className="bg-white border-bottom p-4 d-flex justify-content-between align-items-center sticky-top">
              <div>
                <h3 className="h5 fw-bold mb-1 text-dark">
                  {editRecord ? 'Update Payroll Record' : 'Process New Record'}
                </h3>
                <p className="text-muted small mb-0">Fill in the details below to complete the payroll run.</p>
              </div>
              <button 
                type="button" 
                className="btn-close shadow-none bg-light rounded-circle p-2" 
                onClick={resetForm}
                aria-label="Close"
              ></button>
            </div>

            {/* Drawer Body */}
            <div className="flex-grow-1 overflow-auto p-4 bg-white">
              <form id="payroll-form" onSubmit={handleSubmit}>
                
                {/* 1. Configuration Section */}
                <div className="mb-4">
                  <h4 className="text-muted fw-bold text-uppercase tracking-wider mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    1. Configuration
                  </h4>
                  <div className="bg-light p-3 rounded-3 border">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label small fw-semibold text-dark">
                          Employee <span className="text-danger">*</span>
                        </label>
                        <select 
                          className="form-select border-0 shadow-sm" 
                          name="employee_id" 
                          value={formData.employee_id} 
                          onChange={handleInputChange} 
                          required
                        >
                          <option value="">Select Employee...</option>
                          {employees.map(emp => (
                            <option key={emp.id} value={emp.id}>
                              {emp.first_name} {emp.last_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-sm-12">
                        <label className="form-label small fw-semibold text-dark">Pay Frequency</label>
                        <div className="input-group shadow-sm">
                          <span className="input-group-text bg-white border-0 text-muted">
                            {/* Subtle Icon (SVG) */}
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5z"/></svg>
                          </span>
                          <input 
                            type="text" 
                            className="form-control border-0 text-capitalize bg-white text-muted" 
                            name="pay_frequency" 
                            value={formData.pay_frequency} 
                            readOnly 
                            placeholder="Auto-populated..."
                          />
                        </div>
                      </div>

                      <div className="col-sm-12">
                        <label className="form-label small fw-semibold text-dark">Payroll Period</label>
                        <div className="shadow-sm rounded bg-white">
                          <PeriodWidget 
                            periods={formPeriods} 
                            selectedId={formData.payroll_period_id} 
                            onSelect={handlePeriodChange} 
                            placeholder={formData.pay_frequency ? 'Select Period' : 'Select Frequency First'}
                            disabled={!formData.pay_frequency} 
                            viewYear={viewYear}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Financials & Details Section */}
                <div className="mb-4">
                  <h4 className="text-muted fw-bold text-uppercase mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    2. Financials & Dates
                  </h4>
                  
                  {/* Payroll Timeline Row */}
                  <div className="row g-2 mb-3">
                    <div className="col-12">
                      <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded-3 border text-center">
                        <div className="flex-fill">
                          <span className="d-block small text-muted mb-1">Start Date</span>
                          <strong className="text-dark pr-mono">{formData.period_start_date || '--/--/----'}</strong>
                        </div>
                        <div className="text-muted px-2">
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/></svg>
                        </div>
                        <div className="flex-fill">
                          <span className="d-block small text-muted mb-1">End Date</span>
                          <strong className="text-dark pr-mono">{formData.period_end_date || '--/--/----'}</strong>
                        </div>
                        <div className="text-muted px-2">
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/></svg>
                        </div>
                        <div className="flex-fill border-start border-2 border-primary border-opacity-25 ps-2">
                          <span className="d-block small text-primary fw-bold mb-1">Pay Date</span>
                          <strong className="text-primary pr-mono">{formData.pay_date || '--/--/----'}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Calculations Row */}
                  <div className="row g-3">
                    {!isSalariedEmployee && (
                      <div className="col-sm-6">
                        <label className="form-label small fw-semibold text-dark">Hours Worked</label>
                        <div className="input-group shadow-sm">
                          <input 
                            type="number" 
                            className="form-control border-0" 
                            name="hours_worked" 
                            value={formData.hours_worked} 
                            onChange={handleInputChange} 
                            step="0.5" 
                            placeholder="0.0"
                          />
                          <span className="input-group-text bg-white border-0 text-muted">hrs</span>
                        </div>
                      </div>
                    )}
                    <div className="col-sm-6" style={{ display: isSalariedEmployee ? 'none' : 'block' }}>
                      <label className="form-label small fw-semibold text-dark">Additional Allowance</label>
                      <div className="input-group shadow-sm">
                        <span className="input-group-text bg-white border-0 text-muted">$</span>
                        <input
                          type="number"
                          className="form-control border-0"
                          name="additional_allowance"
                          value={formData.additional_allowance}
                          onChange={handleInputChange}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label small fw-semibold text-dark">Bonus</label>
                      <div className="input-group shadow-sm">
                        <span className="input-group-text bg-white border-0 text-muted">$</span>
                        <input
                          type="number"
                          className="form-control border-0"
                          name="bonus"
                          value={formData.bonus}
                          onChange={handleInputChange}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold text-dark">Gross Pay</label>
                      <div 
                        className={`form-control border shadow-sm d-flex justify-content-between align-items-center ${!formData.gross_pay ? 'bg-light text-muted' : 'bg-primary text-white border-primary'}`} 
                        style={{ minHeight: '40px' }}
                      >
                        <span className="fw-semibold">Total:</span>
                        <span className={!formData.gross_pay ? 'fst-italic small' : 'fw-bold fs-5 pr-mono'}>
                          {formData.gross_pay ? `$${parseFloat(formData.gross_pay).toLocaleString('en-US', {minimumFractionDigits: 2})}` : 'Auto-calculated...'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. Administration Section */}
                <div>
                  <h4 className="text-muted fw-bold text-uppercase mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    3. Administration
                  </h4>
                  <div className="row g-3 bg-light p-3 rounded-3 border mb-2">
                    <div className="col-sm-5">
                      <label className="form-label small fw-semibold text-dark">Status</label>
                      <select 
                        className="form-select border-0 shadow-sm" 
                        name="status" 
                        value={formData.status} 
                        onChange={handleInputChange}
                      >
                        <option value="Draft">Draft (Pending)</option>
                        <option value="Final">Final (Approved)</option>
                      </select>
                    </div>
                    <div className="col-sm-7">
                      <label className="form-label small fw-semibold text-dark">Internal Notes</label>
                      <textarea 
                        className="form-control border-0 shadow-sm" 
                        name="notes" 
                        value={formData.notes} 
                        onChange={handleInputChange} 
                        placeholder="Add optional context or adjustments..." 
                        rows="2"
                        style={{ resize: 'none' }}
                      ></textarea>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Drawer Footer */}
            <div className="bg-white p-4 border-top d-flex gap-3 justify-content-end mt-auto z-3">
              <button 
                type="button" 
                className="btn btn-outline-secondary fw-semibold px-4 rounded-pill" 
                onClick={resetForm}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                form="payroll-form" 
                className="btn btn-primary fw-bold px-5 shadow-sm rounded-pill d-flex align-items-center gap-2"
              >
                {editRecord ? 'Save Changes' : 'Process Record'}
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Breakdown Centered Modal */}
      {viewRecord && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(3px)', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header bg-light border-bottom p-4">
                <h5 className="modal-title fw-bold text-dark">Tax Breakdown: <span className="text-primary">{getEmployeeName(viewRecord.employee_id)}</span></h5>
                <button type="button" className="btn-close shadow-none" onClick={() => { setViewRecord(null); setViewFormData(null); }}></button>
              </div>
              <div className="modal-body p-4 bg-white">
                {viewFormData && (
                  <div className="row g-4">
                    <div className="col-md-6">
                       <label className="form-label small fw-bold text-muted text-uppercase tracking-wide">Gross Pay</label>
                       <div className="input-group shadow-sm">
                         <span className="input-group-text bg-light border-secondary border-opacity-25">$</span>
                         <input type="number" name="gross_pay" value={viewFormData.gross_pay} onChange={handleViewRecordEdit} className="form-control fw-medium pr-mono border-secondary border-opacity-25" disabled={viewRecord.status === 'Final'} />
                       </div>
                    </div>
                    <div className="col-md-6">
                       <label className="form-label small fw-bold text-muted text-uppercase tracking-wide">Federal Tax</label>
                       <div className="input-group shadow-sm">
                         <span className="input-group-text bg-light text-danger border-secondary border-opacity-25">-$</span>
                         <input type="number" name="federal_tax_deduction" value={viewFormData.federal_tax_deduction} onChange={handleViewRecordEdit} className="form-control fw-medium pr-mono border-secondary border-opacity-25" disabled={viewRecord.status === 'Final'} />
                       </div>
                    </div>
                    <div className="col-md-6">
                       <label className="form-label small fw-bold text-muted text-uppercase tracking-wide">Provincial Tax</label>
                       <div className="input-group shadow-sm">
                         <span className="input-group-text bg-light text-danger border-secondary border-opacity-25">-$</span>
                         <input type="number" name="provincial_tax_deduction" value={viewFormData.provincial_tax_deduction} onChange={handleViewRecordEdit} className="form-control fw-medium pr-mono border-secondary border-opacity-25" disabled={viewRecord.status === 'Final'} />
                       </div>
                    </div>
                    <div className="col-md-6">
                       <label className="form-label small fw-bold text-muted text-uppercase tracking-wide">CPP Contribution</label>
                       <div className="input-group shadow-sm">
                         <span className="input-group-text bg-light text-danger border-secondary border-opacity-25">-$</span>
                         <input type="number" name="cpp_contribution" value={viewFormData.cpp_contribution} onChange={handleViewRecordEdit} className="form-control fw-medium pr-mono border-secondary border-opacity-25" disabled={viewRecord.status === 'Final'} />
                       </div>
                    </div>
                    <div className="col-md-6">
                       <label className="form-label small fw-bold text-muted text-uppercase tracking-wide">EI Premium</label>
                       <div className="input-group shadow-sm">
                         <span className="input-group-text bg-light text-danger border-secondary border-opacity-25">-$</span>
                         <input type="number" name="ei_premium" value={viewFormData.ei_premium} onChange={handleViewRecordEdit} className="form-control fw-medium pr-mono border-secondary border-opacity-25" disabled={viewRecord.status === 'Final'} />
                       </div>
                    </div>
                    
                    <div className="col-12"><hr className="my-1 border-light" /></div>

                    <div className="col-md-6">
                      <div className="bg-light p-4 rounded-3 text-center border shadow-sm h-100">
                        <p className="mb-2 small fw-bold text-muted text-uppercase tracking-wide">Total Deductions</p>
                        <h3 className="text-danger fw-bold mb-0 pr-mono">-${viewFormData.total_deductions.toFixed(2)}</h3>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="bg-success bg-opacity-10 p-4 rounded-3 text-center border border-success border-opacity-25 shadow-sm h-100">
                        <p className="mb-2 small fw-bold text-success text-uppercase tracking-wide">Final Net Pay</p>
                        <h3 className="text-success fw-bold mb-0 pr-mono">${viewFormData.net_pay.toFixed(2)}</h3>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light border-top p-3 d-flex justify-content-end">
                <button type="button" className="btn btn-light border px-4 fw-semibold" onClick={() => { setViewRecord(null); setViewFormData(null); }}>Close</button>
                {viewRecord.status !== 'Final' && (
                  <button type="button" className="btn btn-primary px-4 fw-bold shadow-sm" onClick={handleViewRecordSave}>Save Tax Adjustments</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollRecords;